import React, { useEffect, useRef, useState } from 'react';

import { useTheme } from '@/contexts/ThemeContext';
import { useScrollLock } from '@/hooks/useScrollLock';
import { Book, titleToSlug } from '@/utils/bookRouting';
import { isNativeApp } from '@/utils/platform';
import { analytics } from '@/services/analytics';

import BrowseBookCard from '../components/BrowseBookCard';
import SearchClearButton from '../components/SearchClearButton';
import TopNavigation from '../components/TopNavigation';
import { ToolBarIcon } from '../components/icons/ToolBarIcon';
import {
  FacetOption,
  fetchLibraryBooks,
  fetchLibraryFacets,
  LibraryBooksResponse,
} from '../services/booksApi';

interface LibraryPageProps {
  books: Book[];
  initialSearchQuery?: string;
  onBookSelect: (bookSlug: string, source?: string, collectionName?: string) => void;
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onLibrarySearch?: (query: string) => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onToggleMenu: () => void;
  isAuthenticated: boolean;
  isLoading?: boolean;
}

const SORT_OPTIONS = [
  { value: 'featured', label: 'Featured' },
  { value: 'publication_old_to_new', label: 'Publication (Old to New)' },
  { value: 'publication_new_to_old', label: 'Publication (New to Old)' },
  { value: 'title_a_to_z', label: 'Title (A to Z)' },
  { value: 'title_z_to_a', label: 'Title (Z to A)' },
  { value: 'author_a_to_z', label: 'Author (A to Z)' },
  { value: 'author_z_to_a', label: 'Author (Z to A)' },
  { value: 'length_short_to_long', label: 'Length (Short to Long)' },
  { value: 'length_long_to_short', label: 'Length (Long to Short)' },
];

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 400;

const FilterGroup: React.FC<{
  title: string;
  options: FacetOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  isDarkMode: boolean;
}> = ({ title, options, selectedValues, onToggle, isDarkMode }) => (
  <section className="pb-4">
    <h3 className={`mb-4 text-sm font-semibold ${isDarkMode ? 'text-theme-primary-dark opacity-70' : 'text-blue-700'}`}>
      {title}
    </h3>
    <div className="space-y-3">
      {options.map((option) => (
        <label key={option.value} className="flex cursor-pointer items-center justify-between gap-4 text-sm">
          <span className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedValues.includes(option.value)}
              onChange={() => onToggle(option.value)}
              className="h-4 w-4 accent-blue-700"
            />
            <span>{option.label}</span>
          </span>
          <span className="opacity-55">{option.count}</span>
        </label>
      ))}
    </div>
  </section>
);

const LibraryBookSkeleton: React.FC<{ isDarkMode: boolean }> = ({ isDarkMode }) => (
  <div className="flex w-full flex-col items-start">
    <div
      className={`aspect-[0.72] w-full animate-pulse rounded-sm ${
        isDarkMode ? 'bg-gray-800' : 'bg-gray-200'
      }`}
    />
    <div className="mt-4 flex w-full items-center justify-between gap-4">
      <div className={`h-3 w-20 animate-pulse rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
      <div className={`h-3 w-12 animate-pulse rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
    </div>
  </div>
);

const LibraryPage: React.FC<LibraryPageProps> = ({
  books,
  initialSearchQuery = '',
  onBookSelect,
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateToProfile,
  onLibrarySearch,
  onNavigateToMyBooks,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onToggleMenu,
  isAuthenticated,
  isLoading,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const native = isNativeApp();
  // Copy for web visitors who haven't signed up; members already know the pitch.
  const showIntro = !native && !isAuthenticated;

  const [centuryOptions, setCenturyOptions] = useState<FacetOption[]>([]);
  const [durationOptions, setDurationOptions] = useState<FacetOption[]>([]);
  const [facetsLoading, setFacetsLoading] = useState(true);
  const [browseLoading, setBrowseLoading] = useState(true);
  const [browseData, setBrowseData] = useState<LibraryBooksResponse | null>(null);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearchQuery.trim());
  const [selectedCenturies, setSelectedCenturies] = useState<string[]>([]);
  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
  const [sort, setSort] = useState('featured');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const [isFilterDrawerAnimating, setIsFilterDrawerAnimating] = useState(false);
  const [isSortDrawerVisible, setIsSortDrawerVisible] = useState(false);
  const [isSortDrawerAnimating, setIsSortDrawerAnimating] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const browseResultsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const lastTrackedSearchQueryRef = useRef('');
  const activeBrowseRequestRef = useRef<AbortController | null>(null);

  useScrollLock(isFilterDrawerOpen || isSortDrawerOpen);

  useEffect(() => {
    const loadFacets = async () => {
      try {
        setFacetsLoading(true);
        const facetsData = await fetchLibraryFacets();
        setCenturyOptions(facetsData.centuries);
        setDurationOptions(facetsData.durations);
      } catch (error) {
        console.error('Failed to load library metadata:', error);
      } finally {
        setFacetsLoading(false);
      }
    };

    loadFacets();
  }, []);

  useEffect(() => {
    activeBrowseRequestRef.current?.abort();

    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, selectedCenturies, selectedDurations, sort]);

  useEffect(() => {
    setSearchQuery(initialSearchQuery);
  }, [initialSearchQuery]);

  useEffect(() => {
    if (isFilterDrawerOpen) {
      setIsFilterDrawerVisible(true);
      const timer = window.setTimeout(() => setIsFilterDrawerAnimating(true), 10);
      return () => window.clearTimeout(timer);
    }

    setIsFilterDrawerAnimating(false);
    const timer = window.setTimeout(() => setIsFilterDrawerVisible(false), 300);
    return () => window.clearTimeout(timer);
  }, [isFilterDrawerOpen]);

  useEffect(() => {
    if (isSortDrawerOpen) {
      setIsSortDrawerVisible(true);
      const timer = window.setTimeout(() => setIsSortDrawerAnimating(true), 10);
      return () => window.clearTimeout(timer);
    }

    setIsSortDrawerAnimating(false);
    const timer = window.setTimeout(() => setIsSortDrawerVisible(false), 300);
    return () => window.clearTimeout(timer);
  }, [isSortDrawerOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsFilterDrawerOpen(false);
        setIsSortDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    let ignore = false;
    activeBrowseRequestRef.current = abortController;

    const loadBrowseResults = async () => {
      try {
        setBrowseLoading(true);
        setBrowseError(null);

        const data = await fetchLibraryBooks(
          {
            q: debouncedSearchQuery || undefined,
            centuries: selectedCenturies,
            duration: selectedDurations,
            sort,
            page: currentPage,
            pageSize: PAGE_SIZE,
          },
          abortController.signal,
        );

        if (ignore || abortController.signal.aborted) return;

        if (debouncedSearchQuery !== lastTrackedSearchQueryRef.current) {
          lastTrackedSearchQueryRef.current = debouncedSearchQuery;
          if (debouncedSearchQuery) {
            analytics.track('Library Search', {
              query: debouncedSearchQuery,
              result_count: data.total,
              surface: 'library',
              trigger: 'automatic',
              input_method: 'automatic',
            });
          }
        }

        setBrowseData(data);
      } catch (error) {
        if (ignore || abortController.signal.aborted) return;

        console.error('Failed to load browse results:', error);
        analytics.trackErrorShown('library_browse', error, {
          surface: 'library',
          operation: 'library_browse',
        });
        setBrowseError('Browse results are unavailable right now.');
      } finally {
        if (!ignore) {
          setBrowseLoading(false);
        }
        if (activeBrowseRequestRef.current === abortController) {
          activeBrowseRequestRef.current = null;
        }
      }
    };

    loadBrowseResults();

    return () => {
      ignore = true;
      abortController.abort();
      if (activeBrowseRequestRef.current === abortController) {
        activeBrowseRequestRef.current = null;
      }
    };
  }, [currentPage, debouncedSearchQuery, selectedCenturies, selectedDurations, sort]);

  // The pager sits below the grid, so without this a page change swaps the
  // books in while the viewport stays parked at the bottom (most visible on
  // phones, where the grid is many screens tall). Only scroll when the top of
  // the results is hidden (above the viewport or under the fixed nav, whose
  // height is the scroll margin), so layouts that already show it don't jump.
  const goToPage = (page: number) => {
    setCurrentPage(page);
    const results = browseResultsRef.current;
    if (!results) return;
    const navClearance = parseFloat(getComputedStyle(results).scrollMarginTop) || 0;
    if (results.getBoundingClientRect().top < navClearance) {
      results.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };

  const handleBookClick = (book: Book) => {
    const matchingBook = books.find((candidate) => candidate.id === book.id) ?? book;
    onBookSelect(matchingBook.slug || titleToSlug(matchingBook.title), 'library');
  };

  const trackSortChanged = (value: string) => {
    analytics.track('Library Sort Changed', {
      sort: value,
      surface: 'library',
      trigger: 'button',
      input_method: 'unknown',
    });
  };

  const toggleFilter = (value: string, setValues: React.Dispatch<React.SetStateAction<string[]>>) => {
    setValues((currentValues) =>
      currentValues.includes(value)
        ? currentValues.filter((currentValue) => currentValue !== value)
        : [...currentValues, value],
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCenturies([]);
    setSelectedDurations([]);
    setSort('featured');
    setCurrentPage(1);
  };

  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';
  const inputClasses = isDarkMode
    ? 'border-black-800 bg-black-1500 text-theme-primary-dark placeholder:text-black-300'
    : 'border-black-200 bg-true-white text-theme-primary-light placeholder:text-blue-400';
  const sortMenuClasses = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300';
  const sortMenuItemClasses = isDarkMode
    ? 'cursor-pointer text-gray-200'
    : 'cursor-pointer text-theme-primary-light';
  const selectedSortOption = SORT_OPTIONS.find((option) => option.value === sort) ?? SORT_OPTIONS[0];

  const hasActiveFilters =
    searchQuery.trim().length > 0 || selectedCenturies.length > 0 || selectedDurations.length > 0 || sort !== 'featured';
  const showInitialBrowseSkeleton =
    browseLoading &&
    !browseData &&
    !browseError &&
    initialSearchQuery.trim().length === 0 &&
    selectedCenturies.length === 0 &&
    selectedDurations.length === 0 &&
    sort === 'featured' &&
    currentPage === 1;

  const renderFilterPanelContent = (showHeader: boolean) => (
    <>
      <div className={`mb-8 flex items-center gap-4 ${showHeader ? 'justify-between' : 'justify-end'}`}>
        {showHeader ? (
          <h3 className={`text-sm font-bold ${isDarkMode ? 'text-theme-primary-dark opacity-70' : 'text-blue-700'}`}>
            Filters
          </h3>
        ) : null}
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={`text-sm ${isDarkMode ? 'text-theme-primary-dark opacity-70' : 'text-blue-700'}`}
          >
            Clear all
          </button>
        ) : null}
      </div>

      {facetsLoading ? (
        <p className="text-sm opacity-70">Loading filters…</p>
      ) : (
        <div className="space-y-4">
          <FilterGroup
            title="Publication"
            options={centuryOptions}
            selectedValues={selectedCenturies}
            onToggle={(value) => {
              analytics.track('Library Filter Applied', {
                facet: 'century',
                value,
                action: selectedCenturies.includes(value) ? 'removed' : 'added',
                surface: 'library',
                trigger: 'button',
                input_method: 'unknown',
              });
              toggleFilter(value, setSelectedCenturies);
            }}
            isDarkMode={isDarkMode}
          />
          <FilterGroup
            title="Duration"
            options={durationOptions}
            selectedValues={selectedDurations}
            onToggle={(value) => {
              analytics.track('Library Filter Applied', {
                facet: 'duration',
                value,
                action: selectedDurations.includes(value) ? 'removed' : 'added',
                surface: 'library',
                trigger: 'button',
                input_method: 'unknown',
              });
              toggleFilter(value, setSelectedDurations);
            }}
            isDarkMode={isDarkMode}
          />
        </div>
      )}
    </>
  );

  return (
    <div
      className={`w-full transition-colors duration-300 ${themeClasses}`}
      style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}
    >
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onLibrarySearch={onLibrarySearch}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        onNavigateToMyBooks={onNavigateToMyBooks}
        onNavigateToProfile={onNavigateToProfile}
        onBookSelect={onBookSelect}
        showProfileIcon={isAuthenticated}
        isLoading={isLoading}
        activeItem="library"
      />

      <div className="w-full px-6 pt-12 sm:px-8 lg:px-16 xl:px-24 pb-[calc(10rem+var(--safe-bottom))]">
        {/* The app keeps this tight so a phone shows books sooner. On the web
            the content gets the same breathing room below the title whether
            or not the intro paragraph is showing. */}
        <header className={`mx-auto w-full max-w-6xl ${native ? 'mb-8' : 'mb-18'}`}>
          <h1
            className={`portrait-text-app mb-4 text-left text-4xl font-bold sm:text-5xl lg:text-6xl ${
              isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
            }`}
          >
            {/* "Quiet in the Library?" is the setup for the paragraph's
                punchline (conversation is encouraged here) — on its own in the
                app or for a member it is just a question nobody asked. */}
            {showIntro ? 'Quiet in the Library?' : 'Library'}
          </h1>
          {/* Marketing blurb — see ExplorePage: web-visitor copy that only
              costs members and phone screens their first row of books. */}
          {showIntro && (
            <p
              className={`max-w-4xl pt-6 text-md leading-relaxed sm:text-lg md:text-xl ${
                isDarkMode ? 'text-theme-primary-dark' : 'text-blue-700'
              }`}
            >
              In our library, conversation is strongly encouraged. Don&apos;t just listen, interrupt Aleph and ask anything, from the meaning of a single word, to the life of the author, or the setting of the story. Aleph has infinite patience and almost infinite wisdom.
            </p>
          )}
        </header>

        <section className="mx-auto w-full max-w-6xl">
          <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
            <aside className="hidden h-fit rounded-3xl py-6 pr-6 lg:block lg:pt-[1.95rem]">
              {renderFilterPanelContent(true)}
            </aside>

            {/* scroll-mt clears the fixed TopNavigation (h-17 + safe area). */}
            <div ref={browseResultsRef} className="min-w-0 scroll-mt-[calc(4.25rem+var(--safe-top))]">
              <div className="mb-6 rounded-3xl py-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-[1.2]">
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        id="library-search"
                        type="search"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search by title or author"
                        className={`w-full rounded-full border py-2.5 pl-4 pr-11 outline-none ${inputClasses}`}
                      />
                      <SearchClearButton
                        visible={searchQuery.length > 0}
                        onClear={() => {
                          setSearchQuery('');
                          searchInputRef.current?.focus();
                        }}
                        className="absolute right-3 top-1/2 h-6 w-6 -translate-y-1/2"
                      />
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-4 lg:hidden">
                      <button
                        type="button"
                        onClick={() => setIsFilterDrawerOpen(true)}
                        className={`inline-flex items-center gap-4 text-sm transition-opacity cursor-pointer ${
                          isDarkMode
                            ? 'text-theme-primary-dark hover:opacity-75'
                            : 'text-theme-primary-light hover:opacity-75'
                        }`}
                      >
                        <ToolBarIcon size="sm" className="shrink-0" />
                        <span className="font-semibold">Filters</span>
                      </button>

                      <div className="min-w-[200px]">
                        <div className="relative flex justify-end">
                          <button
                            type="button"
                            aria-haspopup="dialog"
                            aria-expanded={isSortDrawerOpen}
                            onClick={() => setIsSortDrawerOpen(true)}
                            className={`inline-flex items-center gap-3 rounded-full px-2 py-2 text-sm transition-colors cursor-pointer ${
                              isDarkMode ? 'hover:bg-gray-800/60' : 'hover:bg-black-100/60'
                            }`}
                          >
                            <span className="opacity-70" aria-hidden="true">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M5 3L5 13"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M2.75 5.25L5 3L7.25 5.25"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M11 3L11 13"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M8.75 10.75L11 13L13.25 10.75"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <span className="font-semibold">{selectedSortOption.label}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="hidden min-w-[200px] lg:block xl:w-[220px]">
                    <div ref={sortMenuRef} className="relative flex justify-end">
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={isSortMenuOpen}
                        onClick={() => setIsSortMenuOpen((open) => !open)}
                        className={`inline-flex items-center gap-3 rounded-full px-2 py-2 text-sm transition-colors cursor-pointer ${
                          isDarkMode ? 'hover:bg-gray-800/60' : 'hover:bg-black-100/60'
                        }`}
                      >
                        <span className="opacity-70" aria-hidden="true">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M5 3L5 13"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <path
                              d="M2.75 5.25L5 3L7.25 5.25"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M11 3L11 13"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                            <path
                              d="M8.75 10.75L11 13L13.25 10.75"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span className="font-semibold">{selectedSortOption.label}</span>
                      </button>

                      {isSortMenuOpen ? (
                        <div
                          className={`absolute right-0 top-full z-20 mt-2 w-64 overflow-hidden rounded-lg border shadow-xl ${sortMenuClasses}`}
                          role="menu"
                          aria-label="Sort options"
                        >
                          {SORT_OPTIONS.map((option, index) => (
                            <React.Fragment key={option.value}>
                              <button
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={sort === option.value}
                                    onClick={() => {
                                      trackSortChanged(option.value);
                                      setSort(option.value);
                                      setIsSortMenuOpen(false);
                                    }}
                                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm ${sortMenuItemClasses}`}
                                  >
                                    <span
                                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                                        isDarkMode ? 'border-gray-400' : 'border-blue-700'
                                      }`}
                                      aria-hidden="true"
                                    >
                                      {sort === option.value ? (
                                        <span className={`h-2 w-2 rounded-full ${isDarkMode ? 'bg-gray-200' : 'bg-blue-700'}`} />
                                      ) : null}
                                    </span>
                                  <span>{option.label}</span>
                              </button>
                            </React.Fragment>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {browseError ? (
                <div className="rounded-3xl p-8">{browseError}</div>
              ) : showInitialBrowseSkeleton ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 md:grid-cols-4 xl:gap-x-7">
                  {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                    <LibraryBookSkeleton key={index} isDarkMode={isDarkMode} />
                  ))}
                </div>
              ) : browseLoading && !browseData ? (
                <div className="rounded-3xl p-8">Loading browse results…</div>
              ) : browseData && browseData.items.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 md:grid-cols-4 xl:gap-x-7">
                    {browseData.items.map((book) => (
                      <BrowseBookCard key={book.id} book={book} onBookClick={handleBookClick} />
                    ))}
                  </div>

                  <div className="mt-10 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      disabled={browseLoading || browseData.page <= 1}
                      onClick={() => goToPage(Math.max(1, browseData.page - 1))}
                      className={`cursor-pointer rounded-full border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40 ${inputClasses}`}
                    >
                      Previous
                    </button>
                    <span className="min-w-24 text-center text-sm opacity-75">
                      {browseLoading ? 'Loading…' : `Page ${browseData.page} of ${browseData.totalPages}`}
                    </span>
                    <button
                      type="button"
                      disabled={browseLoading || browseData.page >= browseData.totalPages}
                      onClick={() => goToPage(browseData.page + 1)}
                      className={`cursor-pointer rounded-full border px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40 ${inputClasses}`}
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <div className="rounded-3xl p-8">
                  No books matched the current search and filters.
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {isFilterDrawerVisible ? (
        <div className="fixed inset-0 z-50 flex lg:hidden" onClick={() => setIsFilterDrawerOpen(false)}>
          <div
            className={`h-full w-full bg-black transition-opacity duration-300 ${isFilterDrawerAnimating ? 'opacity-75' : 'opacity-0'}`}
          />
          <div
            className={`fixed left-0 top-0 h-full w-80 max-w-[86vw] overflow-y-auto shadow-xl transition-transform duration-300 ease-out ${
              isFilterDrawerAnimating ? 'translate-x-0' : '-translate-x-full'
            } ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-6">
              <div className="mb-5 flex items-center justify-between gap-4">
                <span className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-blue-700'}`}>Filters</span>
                <button
                  type="button"
                  onClick={() => setIsFilterDrawerOpen(false)}
                  aria-label="Close filters"
                  className="cursor-pointer transition-opacity hover:opacity-70"
                  style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}
                >
                  ✕
                </button>
              </div>
              {renderFilterPanelContent(false)}
            </div>
          </div>
        </div>
      ) : null}

      {isSortDrawerVisible ? (
        <div className="fixed inset-0 z-50 flex lg:hidden" onClick={() => setIsSortDrawerOpen(false)}>
          <div
            className={`h-full w-full bg-black transition-opacity duration-300 ${isSortDrawerAnimating ? 'opacity-75' : 'opacity-0'}`}
          />
          <div
            className={`fixed right-0 top-0 h-full w-80 max-w-[86vw] overflow-y-auto shadow-xl transition-transform duration-300 ease-out ${
              isSortDrawerAnimating ? 'translate-x-0' : 'translate-x-full'
            } ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-6">
              <div className="mb-5 flex items-center justify-between gap-4">
                <span className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-blue-700'}`}>Sort</span>
                <button
                  type="button"
                  onClick={() => setIsSortDrawerOpen(false)}
                  aria-label="Close sort options"
                  className="cursor-pointer transition-opacity hover:opacity-70"
                  style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }}
                >
                  ✕
                </button>
              </div>
              <div>
                {SORT_OPTIONS.map((option, index) => (
                  <React.Fragment key={option.value}>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={sort === option.value}
                      onClick={() => {
                        trackSortChanged(option.value);
                        setSort(option.value);
                        setIsSortDrawerOpen(false);
                      }}
                      className={`flex w-full cursor-pointer items-center gap-3 pr-4 py-2.5 text-left text-sm ${
                        isDarkMode ? 'text-gray-200' : 'text-theme-primary-light'
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                          isDarkMode ? 'border-gray-400' : 'border-blue-700'
                        }`}
                        aria-hidden="true"
                      >
                        {sort === option.value ? (
                          <span className={`h-2 w-2 rounded-full ${isDarkMode ? 'bg-gray-200' : 'bg-blue-700'}`} />
                        ) : null}
                      </span>
                      <span>{option.label}</span>
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LibraryPage;
